/* eslint-disable no-restricted-globals */
import { round, cloneDeep, isEmpty } from 'lodash';
import {
  CAC_NEW_PROJECT_NAME_LABEL,
  CAC_CORR_MAX_AXES,
  CAC_LEFT_PANEL_BUILD_LABEL,
  CAC_CLUSTER_SEGMENT_INDEX,
  CAC_CLUSTER_BY_RESPONDENTS_DEFAULT_VAL,
  ADD_TO_PASSIVE_EVENT,
  SHOW_HIDE_DATAPOINT_EVENT,
  HANDLE_CLUSTER_BY_SWITCH_EVENT,
  HANDLE_VIEW_SWITCH_EVENT,
  HANDLE_CHANGE_CLUSTER_SIZE,
  HANDLE_CORR_CHANGE_LABEL,
  CAC_CORR_COLUMN_TABLE,
  CAC_CLUSTER_SEG_COMPARISON_TABLE,
  CAC_CLUSTER_DEFAULT_SORT_KEY,
  CAC_CORR_DEFAULT_SORT_KEY_PRIMARY,
  CAC_CORR_DEFAULT_SORT_KEY_SECONDARY,
  CAC_AXES_DISPLAY_FRONT_VIEW,
  CAC_AXES_DISPLAY_TOP_VIEW,
  CAC_AXES_DISPLAY_SIDE_VIEW
} from '../components/corrCluster/CacConstants';

import {
  DV_DEFAULT_VALUE
} from '../constants';

export const CAC_VIEW_PANEL_SWITCH_LIST = [
  { label: 'DATA VIEW', enabled: true },
  { label: 'MAP VIEW', enabled: true },
];

const stripToNumber = val => Number(val.replace(/[%*, ]/g, ''));

const upperCaseIfString = val =>
  typeof val === 'string' || val instanceof String ? val.toUpperCase() : val;

export const SORT_DIRECTION = {
  NONE: 'none',
  ASC: 'asc',
  DESC: 'desc',
};

export function getMapOptions() {
  // All the default mapoptions should be added here only.
  return {
    frontNodeLabelPositions: [],
    frontNodeLabelPositionsCentered: [],
    topNodeLabelPositions: [],
    topNodeLabelPositionsCentered: [],
    sideNodeLabelPositions: [],
    sideNodeLabelPositionsCentered: [],
    showContributionScores: false,
    isCenterChart: true,
    showPassive: true,
    isAxisVisible: true,
    areRowsVisible: true,
    areColsVisible: true,
    showRowLongNames: false,
    showColLongNames: false,
    showPassiveLongNames: false,
  };
}

export function getChartView() {
  return [1, 2];
}

export function getNodeColors() {
  // default node colors
  return {
      col: 'blue',
      row: 'orange',
      passive: 'gray5',
  };
}

// max is nine clusters but make room for 20
export function getClustersOnOff() {
  return new Array(20).fill(true);  // all on by default
}

export function createNewProjectList(list, projectId, activeProjectId) {
  if (!projectId) {
    projectId = 0;
  }
  const defaultBase = [{
    keyword: 'StudyUniverseKey',
    title: 'STUDY UNIVERSE',
    originalTitle: 'STUDY UNIVERSE',
  }];
  const newProjects = list.map(item => {
    let updatedItem = {};
    if (item.details) {
      updatedItem = { ...item, ...item.details };
    } else {
      updatedItem = item;
    }
    /**
     * Make sure all the default project  requirements are made in the below
     * statement
     */
    const weightTypes = updatedItem.weightTypes;
    if (!isEmpty(weightTypes) && weightTypes.dv === undefined) {
      weightTypes.dv = DV_DEFAULT_VALUE;
    }
    const newProject = {
      projectId,
      title: updatedItem.title ? updatedItem.title : CAC_NEW_PROJECT_NAME_LABEL,
      leftPanel: updatedItem.leftPanel,
      study: updatedItem.study,
      weightTypes: updatedItem.weightTypes,
      base: updatedItem.base.length > 0 ? updatedItem.base : defaultBase,
      columns: updatedItem.columns,
      rows: updatedItem.rows,
      corrResponse: {},
      clusterResponse: {},
      mapOptions: updatedItem.mapOptions
        ? updatedItem.mapOptions
        : getMapOptions(),
      isBaseOpen: false,
      isRowsOpen: false,
      isColumnsOpen: false,
      isPassivesOpen: false,
      chartView: updatedItem.chartView
        ? updatedItem.chartView
        : getChartView(),
      updateNodes: false,
      nodeColors: updatedItem.nodeColors
        ? updatedItem.nodeColors
        : getNodeColors(),
      pid: updatedItem.pid ? updatedItem.pid : 0,
      originalTitle: updatedItem.title,
      isProjectSaved: updatedItem.pid !== 0 && updatedItem.pid !== undefined,
      corrMapView: updatedItem.corrMapView ? updatedItem.corrMapView : true,
      clusterMapView: updatedItem.clusterMapView
        ? updatedItem.clusterMapView
        : false,
      selectedTabIndex: 0,
      clusterSegmentCount: 0,
      selectedCluster: -1, // none selected yet (use optimal)
      clusterBy: updatedItem.clusterBy
      ? updatedItem.clusterBy
      : CAC_CLUSTER_BY_RESPONDENTS_DEFAULT_VAL,
      activeTable: {
        corrActiveTable: CAC_CORR_COLUMN_TABLE.toLowerCase(),
        clusterActiveTable: CAC_CLUSTER_SEG_COMPARISON_TABLE,
      },
      sortData: {
        corrTableType: {
          columns: {
            sortOrder: SORT_DIRECTION.DESC,
            key: '',
            columnIndex: 1,
          },
          rows: {
            sortOrder: SORT_DIRECTION.DESC,
            key: '',
            columnIndex: 1,
          },
          axes: {
            sortOrder: SORT_DIRECTION.DESC,
            key: CAC_CLUSTER_DEFAULT_SORT_KEY,
            columnIndex: 3,
          },
        },
        clusterTableType: {
          segment: {},
        },
      },
      clustersOnOff: updatedItem.clustersOnOff
        ? updatedItem.clustersOnOff
        : getClustersOnOff(),
    };
    activeProjectId = projectId;
    projectId += 1;
    return newProject;
  });
  return { newProjects, projectId, activeProjectId };
}

export function getCacActiveProject(projects, activeProjectId) {
  return projects.find(item => item.projectId === activeProjectId);
}

export function getMainHeaderLabels(chartView) {
  const mainHeaderLabels = [{ label: '' }];
  chartView.forEach(item => {
    mainHeaderLabels.push({ label: `Axis ${item}` });
  });
  return mainHeaderLabels;
}

export function getCorrColTableData(cm, columns, chartView) {
  const firstAxisIndex = chartView[0] - 1;
  const secondAxisIndex = chartView[1] - 1;
  const tableData = [];
  const rowLen = cm.colPoints.length;
  for (let i = 0; i < rowLen; i+=1) {
    const rowObj = {};
    // rowObj.columnName = cm.colPoints[i].label;
    rowObj.columnName = cm.colPoints[i].label;
    rowObj.mass = Number(round(cm.colMass[i], 4).toFixed(4));
    rowObj.audience = round(cm.colAudience[i]);
    rowObj.inertia = round(cm.colInertiaPct[i], 4);
    rowObj.coo1 = round(cm.colPoints[i].values[firstAxisIndex] * 100, 1);
    rowObj.cor1 = round(cm.colCorrelation[i].values[firstAxisIndex], 1);
    rowObj.ctr1 = round(cm.colContribution[i].values[firstAxisIndex], 1);
    rowObj.coo2 = round(cm.colPoints[i].values[secondAxisIndex] * 100, 1);
    rowObj.cor2 = round(cm.colCorrelation[i].values[secondAxisIndex], 1);
    rowObj.ctr2 = round(cm.colContribution[i].values[secondAxisIndex], 1);
    tableData.push(rowObj);
  }
  return tableData;
}

export function getCorrRowTableData(cm, rows,chartView) {
  const firstAxisIndex = chartView[0] - 1;
  const secondAxisIndex = chartView[1] - 1;
  const tableData = [];
  const rowLen = cm.rowPoints.length;
  for (let i = 0; i < rowLen; i+=1) {
    const rowObj = {};
    rowObj.rowName = cm.rowPoints[i].label;
    rowObj.mass = Number(round(cm.rowMass[i], 4).toFixed(4));
    rowObj.audience = round(cm.rowAudience[i]);
    rowObj.inertia = round(cm.rowInertiaPct[i], 4);
    rowObj.coo1 = round(cm.rowPoints[i].values[firstAxisIndex] * 100, 1);
    rowObj.cor1 = round(cm.rowCorrelation[i].values[firstAxisIndex], 1);
    rowObj.ctr1 = round(cm.rowContribution[i].values[firstAxisIndex], 1);
    rowObj.coo2 = round(cm.rowPoints[i].values[secondAxisIndex] * 100, 1);
    rowObj.cor2 = round(cm.rowCorrelation[i].values[secondAxisIndex], 1);
    rowObj.ctr2 = round(cm.rowContribution[i].values[secondAxisIndex], 1);
    tableData.push(rowObj);
  }
  return tableData;
}

export function getCorrAxesTableData(cm) {
  const tableData = [];
  for (let i = 0; i < CAC_CORR_MAX_AXES; i+=1) {
    const rowObj = {};
    rowObj.axis = `Axis ${i + 1}`;
    rowObj.eigenValue = cm.eigenValues[i];
    rowObj.cumeEigenValues = cm.cumeEigenValues[i];
    rowObj.varianceExplainedValues = round(cm.varianceExplainedValues[i], 2);
    rowObj.cumeVarianceExplainedValues = round(
      cm.cumeVarianceExplainedValues[i],
      2
    );
    tableData.push(rowObj);
  }
  return tableData;
}

export function getProjectDetailsToSave(project) {
  //  method is the place to filter data needed to save

  const {
    study,
    base,
    columns,
    rows,
    weightTypes,
    leftPanel,
    mapOptions,
    nodeColors,
    clustersOnOff,
    chartView,
    clusterBy,
  } = project;
  // Below logic makes sure we are not saving StudyUniverse as base
  let finalBase = base;
  if (base.length > 0 && base[0] && base[0].keyword === 'StudyUniverseKey') {
    finalBase = [];
  }
  return {
    study,
    base: finalBase,
    columns,
    rows,
    // activeProjectId, -- renove this, check with NIkhil and Ravi
    weightTypes,
    leftPanel, // Check if we need this one?
    mapOptions,
    nodeColors,
    clustersOnOff,
    chartView,
    clusterBy,
  };
}

export function getProjectDetailsFromSavedProjects(openedFileList) {
  /**
   * All the logic to hook back the saved details back to the newly opened project should
   * be done here, In future when new data is added to the projects which is not present in old
   * files need to be added with default values here. Loop throught the list of opened files
   */
  if (openedFileList.length > 0) {
    openedFileList.map(item => {
      if (item.details.base.length === 0) {
        item.details = {
          ...item.details,
          base: [
            {
              keyword: 'StudyUniverseKey',
              title: 'STUDY UNIVERSE',
              originalTitle: 'STUDY UNIVERSE',
            },
          ],
        };
      }
      if (item.details.rows) {
        item.details.rows.map(row => {
          if (row.isShow === undefined) {
            row.isShow = true;
          }
          return row;
        });
      }
      if (item.details.columns) {
        item.details.columns.map(col => {
          if (col.isShow === undefined) {
            col.isShow = true;
          }
          return col;
        });
      }
      if (!item.details.clusterBy) {
        item.details.clusterBy = CAC_CLUSTER_BY_RESPONDENTS_DEFAULT_VAL;
      } 
      if (item.details.mapOptions) {
        item.details.mapOptions = {
          ...getMapOptions(),
          ...item.details.mapOptions,
        };
      } else {
        item.details.mapOptions = getMapOptions();
      }
      if (item.details.nodeColors) {
        item.details.nodeColors = {
          ...getNodeColors(),
          ...item.details.nodeColors,
        };
      } else {
        item.details.nodeColors = getNodeColors();
      }
      if(item.details.clustersOnOff) {
        item.details.clustersOnOff = [
          ...getClustersOnOff(),
          ...item.details.clustersOnOff
        ]
      }
      else {
        item.details.clustersOnOff = getClustersOnOff();
      }
      if (!item.details.chartView || item.details.chartView.length !== 2) {
        item.details.chartView = getChartView();
      }
      return item;
    });
  }
  return openedFileList;
}

export const CAC_COLUMNS_TABLE_FIRST_COL_HEADER_LABELS = [
  {
    key: 'cn',
    label: 'COLUMN NAME',
    text: 'COLUMN NAME',
    compute: upperCaseIfString,
  },
];

export const CAC_ROWS_TABLE_FIRST_COL_HEADER_LABELS = [
  {
    key: 'rn',
    label: 'ROW NAME',
    text: 'ROW NAME',
    compute: upperCaseIfString,
  },
];

export const CAC_TABLE_SUB_HEADER_LABELS = [
  [
    {
      key: 'mass',
      label: 'MASS',
      text: 'MASS',
      compute: undefined,
    },
    {
      key: 'audience',
      label: 'AUDIENCE',
      text: 'AUDIENCE',
      compute: undefined,
    },
    {
      key: 'inertia',
      label: 'INERTIA',
      text: 'INERTIA',
      compute: undefined,
    },
  ],
  [
    {
      key: 'coo1',
      label: 'COO',
      text: 'COO',
      compute: stripToNumber,
    },
    {
      key: 'cor1',
      label: 'COR',
      text: 'COR',
      compute: stripToNumber,
    },
    {
      key: 'ctr1',
      label: 'CTR',
      text: 'CTR',
      compute: stripToNumber,
    },
  ],
  [
    {
      key: 'coo2',
      label: 'COO',
      text: 'COO',
      compute: stripToNumber,
    },
    {
      key: 'cor2',
      label: 'COR',
      text: 'COR',
      compute: stripToNumber,
    },
    {
      key: 'ctr2',
      label: 'CTR',
      text: 'CTR',
      compute: stripToNumber,
    },
  ],
];

// New project minimum data requirements should be added here
export const NEW_PROJECT_MINIMUM_DATA = {
  base: [],
  columns: [],
  rows: [],
  study: {},
  weightTypes: {},
  leftPanel: CAC_LEFT_PANEL_BUILD_LABEL,
  title: '',
  mapOptions: getMapOptions(),
  nodeColors: getNodeColors(),
  chartView: getChartView(),
  clustersOnOff: getClustersOnOff(),
};

export const CAC_AXES_TABLE_HEADER_LABELS = [
  {
    key: 'a',
    label: 'AXIS',
    text: 'AXIS',
    compute: upperCaseIfString,
  },
  {
    key: 'eigenValue',
    label: 'EIGENVALUE',
    text: 'EIGENVALUE',
    compute: stripToNumber,
  },
  {
    key: 'cumeEigenValues',
    label: 'EIGENVALUE CUMULATIVE TOTAL',
    text: 'EIGENVALUE CUMULATIVE TOTAL',
    compute: stripToNumber,
  },
  {
    key: 'varianceExplainedValues',
    label: 'VARIANCE EXPLAINED',
    text: 'VARIANCE EXPLAINED',
    compute: stripToNumber,
  },
  {
    key: 'cumeVarianceExplainedValues',
    label: 'CUMULATIVE VARIANCE EXPLAINED',
    text: 'CUMULATIVE VARIANCE EXPLAINED',
    compute: undefined,
  },
];

export const CAC_CLUSTER_SEG_FIRST_COL_HEADER_LABELS = [
  {
    key: 'statement',
    label: 'STATEMENT',
    text: 'STATEMENT',
    compute: upperCaseIfString,
  },
];

export function getSegmentMainHeaderLabels(clusterSegmentCount) {
  const mainHeaderLabels = [];
  for (let i = 1; i <= clusterSegmentCount; i+=1) {
    mainHeaderLabels.push({ label: '' });
  }
  return mainHeaderLabels;
}

export function getSegmentTableHeaders(segmentData, clusterSegmentCount) {
  const tableHeaders = [];
  for (let i = 0; i < clusterSegmentCount; i+=1) {
    const header = {};
    header.key = `segment${i + 1}`;
    header.label = segmentData[i].segmentName.toUpperCase();
    header.text = segmentData[i].segmentName.toUpperCase();
    header.compute = undefined;
    tableHeaders.push([header]);
  }
  return tableHeaders;
}

export function getClusterSegmentTableData(args) {
  const { segmentData, statements, compareValue } = args;
  const tableData = [];
  const totalRows = statements.length;
  for (let i = 0; i < totalRows; i+=1) {
    const rowObj = {};
    rowObj.statement = statements[i];
    segmentData.forEach((item, index) => {
      rowObj[`segment${index + 1}`] =
        compareValue === CAC_CLUSTER_SEGMENT_INDEX.value
          ? item.targetDetails[i][compareValue]
          : round(item.targetDetails[i][compareValue], 4).toFixed(4);
    });
    tableData.push(rowObj);
  }
  return tableData;
}

export function getApiRequestParameter(project, keyType, index, eventType) {
  switch (eventType) {
    case ADD_TO_PASSIVE_EVENT: 
      if (keyType === 'col') {
        project.columns[index].isPassive = !project.columns[index]
          .isPassive;
      }
      if (keyType === 'row') {
        project.rows[index].isPassive = !project.rows[index]
          .isPassive;
      }
      break;
    case SHOW_HIDE_DATAPOINT_EVENT:
      if (keyType === 'col') {
        project.columns[index].isShow = !project.columns[index]
          .isShow;
      }
      if (keyType === 'row') {
        project.rows[index].isShow = !project.rows[index].isShow;
      }
      break;
    case HANDLE_CLUSTER_BY_SWITCH_EVENT:
      project.clusterBy = keyType;
    break;
    case HANDLE_VIEW_SWITCH_EVENT:
      project.leftPanel = keyType;
    break;
    case HANDLE_CHANGE_CLUSTER_SIZE: 
      project.clusterSegmentCount = index;
    break;
    case HANDLE_CORR_CHANGE_LABEL: {
      const kt = keyType.split('♥');
      if (kt.length === 2) {
        if (kt[0] === "row") {
          project.rows[index].title = kt[1];
        }
        if (kt[0] === "col") {
          project.columns[index].title = kt[1];
        }
      }
      break;
    }
    default: break;
  }

  const { rows, columns } = project;
  const passiveRows = [];
  const passiveCols = [];
  const rowsFilterData = rows && rows.filter(item => item.isShow === true);
  const colsFilterData = columns && columns.filter(item => item.isShow === true);
  if(rowsFilterData) {
    rowsFilterData.forEach((item, index) => {
      if(item.isPassive && item.isShow) passiveRows.push(index)
    });
  }
  if(colsFilterData){
    colsFilterData.forEach((item, index) => {
      if(item.isPassive && item.isShow) passiveCols.push(index);
    });
  }

  const projectObj = {
    ...project,
    passiveRows,
    passiveCols,
  };
  return projectObj;
}

const customCACComparator = (sortKey, sortOrder, primer) => {
  const getCellMetrics = primer
    ? row => primer(row[sortKey])
    : row => row[sortKey];

  const sortOrderNumber = sortOrder === SORT_DIRECTION.ASC ? 1 : -1;

  return (a, b) => {
    const el1 = getCellMetrics(a);
    const el2 = getCellMetrics(b);
    if (isNaN(el1)) {
      return 1;
    }
    if (isNaN(el2)) {
      return -1;
    }
    return sortOrderNumber * ((el1 > el2) - (el2 > el1));
  };
};

export const sortCACTable = (
  SortObj,
  activeTable,
  cellElements,
  ) => {
  const sortKey = SortObj.key;
  const sortOrder = SortObj.sortOrder;
  const primer = cellElements.filter(
    arr => arr.filter(
    cellElement => cellElement.key === sortKey
  )).compute;

  const newRowSubList = activeTable;
  const rowList = [
    ...newRowSubList.sort(
      customCACComparator(sortKey, sortOrder, primer),
  ),
  ];

  return rowList;
};

export function getSortObj(args) {
  const { cm, chartView, sortObj } = args;
  const firstAxisIndex = chartView[0] - 1;
  const secondAxisIndex = chartView[1] - 1;
  const newSortObj = cloneDeep(sortObj);
  if (newSortObj && newSortObj.key === '') {
    newSortObj.key =
      cm.varianceExplainedValues[firstAxisIndex] >
      cm.varianceExplainedValues[secondAxisIndex]
        ? CAC_CORR_DEFAULT_SORT_KEY_PRIMARY
        : CAC_CORR_DEFAULT_SORT_KEY_SECONDARY;
  }  
  return newSortObj;
}

export function hasUnsavedChanges(projects) {
  let result = false;
  result = projects.some(item => item.isProjectSaved === false);
  return result;
} 


export function getChartViewName(chartView) {
  let chartViewName;
  if (chartView[0] === 1 && chartView[1] === 2) {
    chartViewName = CAC_AXES_DISPLAY_FRONT_VIEW;
  } if (chartView[0] === 1 && chartView[1] === 3) {
    chartViewName = CAC_AXES_DISPLAY_TOP_VIEW;
  } if (chartView[0] === 2 && chartView[1] === 3) {
    chartViewName = CAC_AXES_DISPLAY_SIDE_VIEW;
  } 
  return chartViewName;
}